import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import * as gc from "@/growth/api";

import OverviewTab from "@/growth/tabs/OverviewTab";
import BrandBrainTab from "@/growth/tabs/BrandBrainTab";
import AuditTab from "@/growth/tabs/AuditTab";
import VisibilityTab from "@/growth/tabs/VisibilityTab";
import CreativeTab from "@/growth/tabs/CreativeTab";
import ApprovalsTab from "@/growth/tabs/ApprovalsTab";
import AuditLogTab from "@/growth/tabs/AuditLogTab";
import WorkspaceGate from "@/growth/components/WorkspaceGate";

export default function GrowthCenterPage() {
  const { user } = useAuth();
  const [wsId, setWsId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("overview");
  const [wsChecked, setWsChecked] = useState(false);
  const [defaultUrl, setDefaultUrl] = useState("");

  const refreshSummary = async (id = wsId) => {
    if (!id) return;
    const s = await gc.getSummary(id);
    setSummary(s);
    setDefaultUrl(s.workspace?.website_url || "");
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await gc.ensureWorkspace({
          display_name: user.name || user.email?.split("@")[0] || "My Business",
          country: "US",
        });
        setWsId(r.workspace_id);
        await refreshSummary(r.workspace_id);
      } catch { /* not logged in path */ }
      finally { setWsChecked(true); }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-white">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-xl mx-auto py-24 text-center">
          <h1 className="text-4xl font-black text-slate-900">AI Growth Center</h1>
          <p className="text-slate-600 mt-3">Sign in to open your workspace.</p>
          <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800 rounded-full">
            <a href="/login?redirect=/growth-center" data-testid="gc-signin-btn">Sign in</a>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!wsChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-white">Loading workspace…</div>;
  }

  if (!wsId) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <WorkspaceGate user={user} onReady={async (id, url) => { setWsId(id); setDefaultUrl(url); await refreshSummary(id); }} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="gc-root">
        <header className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold uppercase tracking-widest">AI Growth Center</div>
          <h1 className="text-4xl font-black text-slate-900 mt-3">{summary?.workspace?.display_name || "Workspace"}</h1>
          <p className="text-slate-600 mt-1 text-sm max-w-3xl">A human-supervised loop that connects your brand knowledge, website audit, AI visibility and creative production — with every consequential action gated by your approval.</p>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 mb-6 flex flex-wrap h-auto">
            <TabsTrigger value="overview" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="brand" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-brand">Brand Brain</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-audit">Growth Audit</TabsTrigger>
            <TabsTrigger value="visibility" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-visibility">AI Visibility</TabsTrigger>
            <TabsTrigger value="creative" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-creative">Creative Studio</TabsTrigger>
            <TabsTrigger value="approvals" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-approvals">Approvals</TabsTrigger>
            <TabsTrigger value="log" className="rounded-full text-xs px-4 py-1.5" data-testid="gc-tab-log">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab summary={summary} onGoto={setTab} /></TabsContent>
          <TabsContent value="brand"><BrandBrainTab wsId={wsId} onChanged={() => refreshSummary()} /></TabsContent>
          <TabsContent value="audit"><AuditTab wsId={wsId} defaultUrl={defaultUrl} onChanged={() => refreshSummary()} /></TabsContent>
          <TabsContent value="visibility"><VisibilityTab wsId={wsId} onChanged={() => refreshSummary()} /></TabsContent>
          <TabsContent value="creative"><CreativeTab wsId={wsId} onChanged={() => refreshSummary()} /></TabsContent>
          <TabsContent value="approvals"><ApprovalsTab wsId={wsId} onChanged={() => refreshSummary()} /></TabsContent>
          <TabsContent value="log"><AuditLogTab wsId={wsId} /></TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
